<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { fly, fade } from 'svelte/transition';
  import {
    showAIChat,
    aiChatActive,
    aiChatMessages,
    aiChatFilters,
    aiChatRefinementQuery
  } from '$lib/stores/ai';
  import { queryFilters } from '$lib/stores/selection';
  import { submitAssistantMessage, applyAIFilters, applyAIIndustryMatches } from '$lib/api/ai';
  import { describeSelection } from '$lib/filters/describe';
  import { submitFilters, getDefaultFilters } from '$lib/api/filters';
  import { selectedIndustryCentroids } from '$lib/stores/data';
  import { mixedResults } from '$lib/stores/mixedResults';
  import { handleError } from '$lib/utils/error-handling';
  import { notifications } from '$lib/stores/notifications';
  import { aiModeActive } from '$lib/stores/ai';
  import { get } from 'svelte/store';
  import { onMount } from 'svelte';
  import type { AssistantResponse } from '$lib/types/ai';

  export let fullWidth = false;
  export let onSearchExecuted: (() => void) | undefined = undefined;

  let inputText = '';
  let isLoading = false;
  let messagesContainer: HTMLDivElement;
  let textareaEl: HTMLTextAreaElement;
  let welcomePhase: 'idle' | 'greeting' | 'typing' | 'done' = 'idle';
  let showSuggestions = false;

  // Staged welcome: greeting → typing indicator → welcome message
  onMount(() => {
    if ($aiChatMessages.length === 0) {
      adoptTheCurrentSelection();
      runWelcomeSequence();
    } else {
      welcomePhase = 'done';
    }
  });

  /**
   * Start the conversation from what the customer already has.
   *
   * The assistant is reachable from the data view, on top of a selection that
   * has usually taken some work to build — and it used to open blind. Its own
   * `aiChatFilters` started empty and that empty object was what travelled to
   * the model as "Current Filter State", so "make it only Zurich" threw away
   * every industry, size and role the customer had chosen and searched for
   * companies in Zurich instead. Only the industry centroids came along, and
   * only because the request reads those from the live store directly.
   *
   * Seeding the conversation with the live filters makes the model's own merge
   * do the right thing: it returns the fields it changed, they are overlaid on
   * the rest, and refining means refining rather than starting over.
   */
  function adoptTheCurrentSelection() {
    // Through `unknown`, because the conversation's copy is a loose bag the
    // model's partial updates are merged into by key — the same shape, minus
    // the type's promise about which keys exist.
    const live = get(queryFilters);
    aiChatFilters.set(structuredClone(live) as unknown as Record<string, unknown>);
    if (live.embedding_query) aiChatRefinementQuery.set(live.embedding_query);
  }

  function runWelcomeSequence() {
    // Phase 1: Show greeting
    welcomePhase = 'greeting';
    aiChatMessages.set([{ role: 'assistant', content: get(_)('ai.chat.welcomeTitle') }]);

    // Phase 2: Show typing indicator after a short pause
    setTimeout(() => {
      welcomePhase = 'typing';
      scrollToBottom();
    }, 800);

    // Phase 3: Add the welcome message
    setTimeout(() => {
      welcomePhase = 'done';
      aiChatMessages.update((m) => [
        ...m,
        { role: 'assistant', content: get(_)('ai.chat.welcomeMessage') }
      ]);
      scrollToBottom();
      announceTheSelection();
    }, 2000);
  }

  /**
   * Say back what it was handed.
   *
   * Being given the current filters silently is worse than not having them: the
   * customer cannot tell whether the thing they are about to refine is the
   * selection on screen or a blank slate, and the difference only shows up
   * after a search has run on the wrong one.
   *
   * Written here rather than asked of the model. It is a fact the browser
   * already holds, so a round trip could only make it slower, cost a turn of
   * budget, and occasionally get it wrong.
   */
  function announceTheSelection() {
    const lines = describeSelection(get(_));
    if (lines.length === 0) return;

    setTimeout(() => {
      const summary = lines.map((line) => `• ${line.label}: ${line.value}`).join('\n');
      aiChatMessages.update((m) => [
        ...m,
        {
          role: 'assistant',
          content: `${get(_)('assistant.state.intro')}\n${summary}\n\n${get(_)('assistant.state.outro')}`
        }
      ]);
      scrollToBottom();
    }, 900);
  }

  function seedWelcomeMessage() {
    runWelcomeSequence();
  }

  function scrollToBottom() {
    if (messagesContainer) {
      setTimeout(() => {
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
      }, 50);
    }
  }

  function autoResize() {
    if (textareaEl) {
      textareaEl.style.height = 'auto';
      const maxH = fullWidth ? 120 : 80;
      textareaEl.style.height = Math.min(textareaEl.scrollHeight, maxH) + 'px';
    }
  }

  async function sendMessage() {
    const text = inputText.trim();
    if (!text || isLoading) return;

    inputText = '';
    if (textareaEl) textareaEl.style.height = 'auto';
    aiChatMessages.update((m) => [...m, { role: 'user', content: text }]);
    aiChatActive.set(true);
    isLoading = true;
    scrollToBottom();

    let searchWasExecuted = false;
    let filtersWereReset = false;

    try {
      const currentMessages = get(aiChatMessages);
      const currentFilters = get(aiChatFilters);
      const currentRefinementQuery = get(aiChatRefinementQuery);

      const response: AssistantResponse = await submitAssistantMessage({
        messages: currentMessages,
        filters: currentFilters,
        industry_queries: [],
        refinement_query: currentRefinementQuery
      });

      // Capture search_executed flag early before any potentially-failing operations
      searchWasExecuted = !!response.search_executed;

      // Update accumulated state
      if (response.refinement_query !== undefined) {
        aiChatRefinementQuery.set(response.refinement_query);
      }
      if (response.filters) {
        // Detect if backend is signaling a full reset (all arrays empty, ranges null)
        const isReset = Object.values(response.filters).every(
          (v) =>
            v === null ||
            v === undefined ||
            v === '' ||
            (Array.isArray(v) && v.length === 0) ||
            (Array.isArray(v) && v.every((x: unknown) => x === null))
        );

        if (isReset && Object.keys(response.filters).length > 0) {
          // Full reset: clear all accumulated state
          filtersWereReset = true;
          aiChatFilters.set({});
          queryFilters.set(getDefaultFilters());
          selectedIndustryCentroids.set(new Map());
          mixedResults.set({ companies: [], metadata: {}, similarityBatches: [], loading: false });
          aiChatRefinementQuery.set('');
        } else {
          // Merge into accumulated filters (don't replace — preserves fields the backend didn't return)
          aiChatFilters.update((prev) => {
            const merged = { ...prev };
            for (const [k, v] of Object.entries(response.filters)) {
              // Only overwrite arrays when the new value is non-empty
              if (Array.isArray(v)) {
                if (v.length > 0) merged[k] = v;
              } else if (v !== null && v !== undefined) {
                merged[k] = v;
              }
            }
            return merged;
          });
          applyAIFilters(response.filters, get(aiChatRefinementQuery));
        }
      }
      if (response.industry_matches && response.industry_matches.length > 0) {
        await applyAIIndustryMatches(response.industry_matches, !!response.replace_industries);
      }

      // Add assistant message
      aiChatMessages.update((m) => [...m, { role: 'assistant', content: response.message }]);
      scrollToBottom();

      // If search was executed and filters weren't just reset, apply full accumulated filters and search
      if (searchWasExecuted && !filtersWereReset) {
        // Apply the full accumulated filters (not just this turn's) to ensure all conversation context is used
        applyAIFilters(get(aiChatFilters), get(aiChatRefinementQuery));
        await submitFilters();
      }
    } catch (error) {
      const errorMessage = await handleError(error, {
        operation: 'submitAssistantMessage',
        component: 'ai-chat'
      });
      notifications.error(errorMessage);
      aiChatMessages.update((m) => [
        ...m,
        { role: 'assistant', content: $_('ai.chat.errorMessage') }
      ]);
      scrollToBottom();
    } finally {
      isLoading = false;
      // Focus textarea for continued conversation if no search was executed
      if (!searchWasExecuted && textareaEl) {
        setTimeout(() => textareaEl?.focus(), 50);
      }
      // Always transition after search, even if intermediate steps failed
      if (searchWasExecuted) {
        showAIChat.set(false);
        // Transition to classic mode (handled by onSearchExecuted callback or directly)
        if (onSearchExecuted) {
          onSearchExecuted();
        } else if (fullWidth) {
          aiModeActive.set(false);
        }
      }
    }
  }

  export function resetChat() {
    aiChatMessages.set([]);
    aiChatFilters.set({});
    aiChatRefinementQuery.set('');
    aiChatActive.set(false);
    queryFilters.set(getDefaultFilters());
    selectedIndustryCentroids.set(new Map());
    mixedResults.set({ companies: [], metadata: {}, similarityBatches: [], loading: false });
    seedWelcomeMessage();
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  }
</script>

<!-- Messages area -->
<div
  class="flex-1 overflow-y-auto {fullWidth ? 'px-6 py-6' : 'bg-surface-canvas px-4 py-4'}"
  bind:this={messagesContainer}
>
  <!-- Chat messages -->
  <div class={fullWidth ? 'mx-auto max-w-2xl space-y-4' : 'space-y-3'}>
    {#each $aiChatMessages as message, idx}
      <div
        class="flex {message.role === 'user' ? 'justify-end' : 'justify-start'}"
        in:fly={{ y: 8, duration: 200, delay: idx === $aiChatMessages.length - 1 ? 50 : 0 }}
      >
        {#if message.role === 'assistant'}
          <div class="flex max-w-[88%] gap-2">
            <div
              class="mt-1 flex flex-shrink-0 items-center justify-center rounded-surface bg-accent-50 {fullWidth
                ? 'h-8 w-8'
                : 'h-6 w-6'}"
            >
              <svg
                class="{fullWidth ? 'h-4.5 w-4.5' : 'h-3.5 w-3.5'} text-accent-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
            <div
              class="rounded-surface border border-hairline bg-white {fullWidth
                ? 'px-4 py-3 text-sm'
                : 'px-3.5 py-2.5 text-[13px]'} whitespace-pre-line leading-relaxed text-gray-800"
            >
              {message.content}
            </div>
          </div>
        {:else}
          <div
            class="max-w-[85%] rounded-surface bg-accent-600 leading-relaxed text-white {fullWidth
              ? 'px-4 py-3 text-sm'
              : 'px-3.5 py-2.5 text-[13px]'}"
          >
            {message.content}
          </div>
        {/if}
      </div>
    {/each}
    {#if isLoading || welcomePhase === 'typing'}
      <div class="flex justify-start" in:fade={{ duration: 150 }}>
        <div class="flex max-w-[88%] gap-2">
          <div
            class="mt-1 flex flex-shrink-0 items-center justify-center rounded-surface bg-accent-50 {fullWidth
              ? 'h-8 w-8'
              : 'h-6 w-6'}"
          >
            <svg
              class="{fullWidth ? 'h-4.5 w-4.5' : 'h-3.5 w-3.5'} text-accent-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
          </div>
          <div
            class="flex items-center gap-1.5 rounded-surface border border-hairline bg-white {fullWidth
              ? 'px-5 py-4'
              : 'px-4 py-3'}"
          >
            <span class="ai-dot h-1.5 w-1.5 rounded-full bg-gray-300"></span>
            <span
              class="ai-dot h-1.5 w-1.5 rounded-full bg-gray-300"
              style="animation-delay: 160ms;"
            ></span>
            <span
              class="ai-dot h-1.5 w-1.5 rounded-full bg-gray-300"
              style="animation-delay: 320ms;"
            ></span>
          </div>
        </div>
      </div>
    {/if}

    <!-- Expandable suggestions -->
    {#if welcomePhase === 'done' && $aiChatMessages.every((m) => m.role === 'assistant') && !isLoading}
      <div class={fullWidth ? 'mt-4' : 'mt-3'} in:fly={{ y: 8, duration: 200, delay: 200 }}>
        <button
          type="button"
          class="flex items-center gap-1.5 rounded-control px-3 py-1.5 text-xs text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-600"
          on:click={() => (showSuggestions = !showSuggestions)}
        >
          <svg
            class="h-3.5 w-3.5 transition-transform duration-200 {showSuggestions
              ? 'rotate-90'
              : ''}"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 5l7 7-7 7"
            />
          </svg>
          {$_('ai.chat.showExamples')}
        </button>
        {#if showSuggestions}
          <div
            class={fullWidth
              ? 'mt-2 grid w-full max-w-xl grid-cols-1 gap-3 sm:grid-cols-3'
              : 'mt-2 w-full space-y-2'}
            in:fly={{ y: 4, duration: 150 }}
          >
            {#each [$_('ai.chat.suggestion1'), $_('ai.chat.suggestion2'), $_('ai.chat.suggestion3')] as suggestion, i}
              <button
                type="button"
                class="group flex w-full items-start gap-2.5 rounded-surface border border-hairline bg-white text-left text-gray-700 transition-colors duration-200 hover:border-accent-400 hover:bg-surface-canvas {fullWidth
                  ? 'px-4 py-3 text-sm'
                  : 'px-3.5 py-2.5 text-xs'}"
                on:click={() => {
                  inputText = suggestion;
                  sendMessage();
                }}
              >
                <span
                  class="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-surface bg-accent-50 text-[10px] font-semibold text-accent-600"
                >
                  {i + 1}
                </span>
                <span class="leading-relaxed">{suggestion}</span>
              </button>
            {/each}
          </div>
        {/if}
      </div>
    {/if}
  </div>
</div>

<!-- Input area -->
<div
  class={fullWidth
    ? 'border-t border-hairline bg-white px-6 py-4'
    : 'border-t border-hairline bg-white px-3 py-2.5'}
>
  <div class={fullWidth ? 'mx-auto flex max-w-2xl items-end gap-3' : 'flex items-end gap-2'}>
    <div class="relative flex-1">
      <textarea
        bind:this={textareaEl}
        class="w-full resize-none rounded-control border border-hairline bg-surface-canvas pr-3 leading-relaxed transition-all duration-200 placeholder:text-gray-400 focus:border-accent-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-accent-100 {fullWidth
          ? 'px-4 py-3 text-sm'
          : 'px-3.5 py-2.5 text-[13px]'}"
        placeholder={$_('ai.chat.placeholder')}
        bind:value={inputText}
        on:keydown={handleKeydown}
        on:input={autoResize}
        rows="1"
        disabled={isLoading}
        style="min-height: {fullWidth ? '48px' : '40px'}; max-height: {fullWidth
          ? '120px'
          : '80px'};"></textarea>
    </div>
    <button
      type="button"
      on:click={sendMessage}
      disabled={!inputText.trim() || isLoading}
      class="flex flex-shrink-0 items-center justify-center rounded-control bg-accent-600 text-white transition-colors hover:bg-accent-700 disabled:cursor-not-allowed disabled:bg-gray-200 {fullWidth
        ? 'h-12 w-12'
        : 'h-10 w-10'}"
      aria-label={$_('a11y.sendMessage')}
    >
      <svg
        class="{fullWidth
          ? 'h-5 w-5'
          : 'h-4 w-4'} transition-transform duration-200 {inputText.trim() && !isLoading
          ? '-translate-y-0.5 translate-x-0.5'
          : ''}"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        stroke-width="2"
      >
        <path stroke-linecap="round" stroke-linejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
      </svg>
    </button>
  </div>
  <p class="mt-1.5 text-center text-[10px] text-gray-400">{$_('ai.chat.disclaimer')}</p>
</div>

<style>
  /* Typing dots animation */
  .ai-dot {
    animation: ai-pulse 1.4s ease-in-out infinite;
  }

  @keyframes ai-pulse {
    0%,
    80%,
    100% {
      opacity: 0.3;
      transform: scale(0.8);
    }
    40% {
      opacity: 1;
      transform: scale(1);
    }
  }
</style>
